// src/admin/WhatsAppOrderPlugin.js
import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Save, AlertCircle, CheckCircle, Zap, Key, Phone, FileText,
  ToggleLeft, ToggleRight, Send, TestTube, Copy, Info,
  ExternalLink, RefreshCw, Eye, EyeOff, Shield, MessageSquare,
  Clock, Trash2, Plus
} from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import SuccessModal from '../components/SuccessModal';

const WhatsAppOrderPlugin = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showSuccess, setShowSuccess] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [copied, setCopied] = useState('');
  const [testData, setTestData] = useState({
    name: 'Ahmed Khan',
    orderId: 'TEST12345',
    total: '2500',
  });

  // ✅ Default Config
  const defaultConfig = {
    // Plugin Master
    orderConfirmationEnabled: false,

    // Meta Cloud API
    accessToken: '',
    phoneNumberId: '',
    businessNumber: '',

    // Template
    templateName: 'order_confirmation',
    templateLanguage: 'en_US',

    // Message Template Variables
    templateVariables: [
      { key: '{name}', label: 'Customer Name', value: '{name}' },
      { key: '{orderId}', label: 'Order ID', value: '{orderId}' },
      { key: '{total}', label: 'Total Amount', value: '{total}' },
    ],

    // Custom Message Body (for reference/UI)
    messageTemplate: 'Hello {name}!\n\nAapka order confirm ho gaya hai 🎉\n\nOrder ID: {orderId}\nTotal: Rs. {total}\n\nShukriya!\nNAVIROSE',

    // Advanced
    sendOnStatuses: ['processing'], // Kis status par bhejni hai
    retryOnFailure: true,
    notifyAdminOnFailure: true,
  };

  const [config, setConfig] = useState(defaultConfig);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const docRef = doc(db, 'settings', 'whatsapp-order');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setConfig({ ...defaultConfig, ...docSnap.data() });
        }
      } catch (error) {
        console.error('Error fetching config:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  // ================= SAVE =================
  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await setDoc(doc(db, 'settings', 'whatsapp-order'), config, { merge: true });
      setMessage({ type: 'success', text: 'Plugin settings saved!' });
      setShowSuccess(true);
      window.dispatchEvent(new Event('whatsappOrderUpdated'));
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Save error:', error);
      setMessage({ type: 'error', text: 'Failed to save. Try again.' });
    } finally {
      setSaving(false);
    }
  };

  // ================= TEST MESSAGE =================
  const handleTestMessage = async () => {
    if (!testPhone) {
      setMessage({ type: 'error', text: 'Test phone number daalein (with country code).' });
      return;
    }
    if (!config.accessToken || !config.phoneNumberId) {
      setMessage({ type: 'error', text: 'Access Token aur Phone Number ID pehle save karein.' });
      return;
    }

    setTesting(true);
    setMessage({ type: '', text: '' });

    try {
      const cleanPhone = testPhone.replace(/[^0-9]/g, '');

      const response = await fetch(
        `https://graph.facebook.com/v20.0/${config.phoneNumberId}/messages`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${config.accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            messaging_product: 'whatsapp',
            to: cleanPhone,
            type: 'text',
            text: {
              body: `✅ NAVIROSE Test Message\n\nName: ${testData.name}\nOrder: ${testData.orderId}\nTotal: Rs. ${testData.total}\n\nYeh WhatsApp integration test hai!`,
            },
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: '✅ Test message sent! WhatsApp check karein.' });
      } else {
        setMessage({ type: 'error', text: `❌ ${data.error?.message || 'Test failed'}` });
      }
    } catch (error) {
      setMessage({ type: 'error', text: `❌ ${error.message}` });
    } finally {
      setTesting(false);
    }
  };

  // ================= COPY =================
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 1500);
  };

  // ================= ADD VARIABLE =================
  const addVariable = () => {
    setConfig({
      ...config,
      templateVariables: [
        ...config.templateVariables,
        { key: '', label: '', value: '' },
      ],
    });
  };

  const updateVariable = (index, field, value) => {
    const updated = [...config.templateVariables];
    updated[index][field] = value;
    setConfig({ ...config, templateVariables: updated });
  };

  const removeVariable = (index) => {
    setConfig({
      ...config,
      templateVariables: config.templateVariables.filter((_, i) => i !== index),
    });
  };

  if (loading) {
    return (
      <div className="text-center py-16">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-green-600 border-t-transparent"></div>
        <p className="mt-4 text-gray-500">Loading plugin...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl">

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <FaWhatsapp className="text-green-600" size={28} /> WhatsApp Order Confirmation
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Order place hone par customer ko automatic WhatsApp message bhejein.
          </p>
        </div>

        {/* Master Toggle */}
        <button
          type="button"
          onClick={() => setConfig({ ...config, orderConfirmationEnabled: !config.orderConfirmationEnabled })}
          className={`flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-sm transition shadow-md ${
            config.orderConfirmationEnabled
              ? 'bg-green-600 text-white'
              : 'bg-gray-200 text-gray-600'
          }`}
        >
          {config.orderConfirmationEnabled ? <ToggleRight size={22} /> : <ToggleLeft size={22} />}
          {config.orderConfirmationEnabled ? 'PLUGIN ACTIVE' : 'PLUGIN DISABLED'}
        </button>
      </div>

      {message.text && (
        <div className={`text-sm p-3 rounded-lg flex items-center gap-2 ${
          message.type === 'success'
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {message.text}
        </div>
      )}

      {/* Setup Guide */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-2">
          <Info size={18} className="text-blue-600 mt-0.5 shrink-0" />
          <div className="text-xs text-blue-800 leading-relaxed">
            <p className="font-bold mb-1">📖 Setup Guide:</p>
            <ol className="list-decimal ml-4 space-y-1">
              <li>
                <a href="https://developers.facebook.com/apps/" target="_blank" rel="noopener noreferrer" className="underline font-semibold">
                  Meta Developer Console
                </a> par jayein
              </li>
              <li>New App → Business → WhatsApp product add karein</li>
              <li><b>Access Token</b> aur <b>Phone Number ID</b> copy karein</li>
              <li>Message Template banayein (Meta Console mein approved karwayein)</li>
              <li>Neeche details paste karein aur <b>Save</b> dabayein</li>
            </ol>
          </div>
        </div>
      </div>

      <form onSubmit={handleSave} className="space-y-6">

        {/* ============ API CREDENTIALS ============ */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-5 flex items-center gap-2">
            <Key size={20} className="text-purple-600" /> API Credentials
          </h2>

          <div className="space-y-4">

            {/* Access Token */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-gray-700">
                  Access Token (API Key) *
                </label>
                <button
                  type="button"
                  onClick={() => setShowToken(!showToken)}
                  className="text-xs text-purple-600 font-semibold hover:underline flex items-center gap-1"
                >
                  {showToken ? <><EyeOff size={12} /> Hide</> : <><Eye size={12} /> Show</>}
                </button>
              </div>
              <div className="relative">
                <input
                  type={showToken ? 'text' : 'password'}
                  value={config.accessToken}
                  onChange={(e) => setConfig({ ...config, accessToken: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg py-3 pl-4 pr-12 focus:outline-none focus:border-purple-600 text-sm font-mono"
                  placeholder="EAAG... (long string from Meta)"
                />
                {config.accessToken && (
                  <button
                    type="button"
                    onClick={() => copyToClipboard(config.accessToken, 'token')}
                    className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                  >
                    {copied === 'token' ? <CheckCircle size={18} className="text-green-600" /> : <Copy size={18} />}
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Meta Developer → WhatsApp → API Setup → Access Token
              </p>
            </div>

            {/* Phone Number ID */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Phone Number ID *
              </label>
              <input
                type="text"
                value={config.phoneNumberId}
                onChange={(e) => setConfig({ ...config, phoneNumberId: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-purple-600 text-sm font-mono"
                placeholder="123456789012345"
              />
              <p className="text-xs text-gray-500 mt-1">
                Meta Console → WhatsApp → API Setup → "From" number ID
              </p>
            </div>

            {/* Business Number */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Business WhatsApp Number
              </label>
              <input
                type="tel"
                value={config.businessNumber}
                onChange={(e) => setConfig({ ...config, businessNumber: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-purple-600 text-sm"
                placeholder="+92 300 1234567"
              />
            </div>

          </div>
        </div>

        {/* ============ TEMPLATE SETTINGS ============ */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-5 flex items-center gap-2">
            <FileText size={20} className="text-purple-600" /> Message Template
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Template Name *
              </label>
              <input
                type="text"
                value={config.templateName}
                onChange={(e) => setConfig({ ...config, templateName: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-purple-600 text-sm font-mono"
                placeholder="order_confirmation"
              />
              <p className="text-xs text-gray-500 mt-1">
                Meta Console → Message Templates → Approved template name
              </p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Language Code
              </label>
              <select
                value={config.templateLanguage}
                onChange={(e) => setConfig({ ...config, templateLanguage: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-purple-600 text-sm bg-white"
              >
                <option value="en_US">English (US)</option>
                <option value="en_GB">English (UK)</option>
                <option value="ur">Urdu</option>
                <option value="hi">Hindi</option>
                <option value="ar">Arabic</option>
              </select>
            </div>
          </div>

          {/* Message Body Template */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Message Body Template
            </label>
            <textarea
              value={config.messageTemplate}
              onChange={(e) => setConfig({ ...config, messageTemplate: e.target.value })}
              rows={6}
              className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-purple-600 text-sm font-mono resize-none"
              placeholder="Hello {name}! Aapka order confirm ho gaya..."
            />
            <p className="text-xs text-gray-500 mt-1">
              Variables use karein: <code className="bg-gray-100 px-1.5 py-0.5 rounded">{'{name}'}</code>, <code className="bg-gray-100 px-1.5 py-0.5 rounded">{'{orderId}'}</code>, <code className="bg-gray-100 px-1.5 py-0.5 rounded">{'{total}'}</code>
            </p>
          </div>

          {/* Variables */}
          <div className="mt-5">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-semibold text-gray-700">
                Template Variables
              </label>
              <button type="button" onClick={addVariable}
                className="text-xs font-semibold text-purple-600 hover:text-purple-700 flex items-center gap-1">
                <Plus size={12} /> Add Variable
              </button>
            </div>

            <div className="space-y-2">
              {config.templateVariables.map((v, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 p-2 bg-gray-50 rounded-lg">
                  <input
                    type="text"
                    value={v.key}
                    onChange={(e) => updateVariable(i, 'key', e.target.value)}
                    className="col-span-3 border border-gray-300 rounded-lg py-2 px-2 text-xs focus:outline-none focus:border-purple-600 font-mono"
                    placeholder="{var}"
                  />
                  <input
                    type="text"
                    value={v.label}
                    onChange={(e) => updateVariable(i, 'label', e.target.value)}
                    className="col-span-4 border border-gray-300 rounded-lg py-2 px-2 text-xs focus:outline-none focus:border-purple-600"
                    placeholder="Label"
                  />
                  <input
                    type="text"
                    value={v.value}
                    onChange={(e) => updateVariable(i, 'value', e.target.value)}
                    className="col-span-4 border border-gray-300 rounded-lg py-2 px-2 text-xs focus:outline-none focus:border-purple-600 font-mono"
                    placeholder="Value"
                  />
                  <button type="button" onClick={() => removeVariable(i)}
                    className="col-span-1 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg flex items-center justify-center">
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* ============ ADVANCED SETTINGS ============ */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-5 flex items-center gap-2">
            <Shield size={20} className="text-purple-600" /> Advanced Settings
          </h2>

          <div className="space-y-4">

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Send on Order Status (comma separated)
              </label>
              <input
                type="text"
                value={config.sendOnStatuses.join(', ')}
                onChange={(e) => setConfig({
                  ...config,
                  sendOnStatuses: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                })}
                className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-purple-600 text-sm font-mono"
                placeholder="processing, confirmed"
              />
              <p className="text-xs text-gray-500 mt-1">
                Kaunse order status par WhatsApp bhejni hai
              </p>
            </div>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={config.retryOnFailure}
                onChange={(e) => setConfig({ ...config, retryOnFailure: e.target.checked })}
                className="w-5 h-5 text-purple-600"
              />
              <span className="text-sm font-semibold text-gray-700">
                Retry on failure (3 attempts)
              </span>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={config.notifyAdminOnFailure}
                onChange={(e) => setConfig({ ...config, notifyAdminOnFailure: e.target.checked })}
                className="w-5 h-5 text-purple-600"
              />
              <span className="text-sm font-semibold text-gray-700">
                Notify admin if WhatsApp fails
              </span>
            </label>

          </div>
        </div>

        {/* ============ TEST SECTION ============ */}
        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-6">
          <h2 className="text-lg font-bold text-yellow-900 mb-4 flex items-center gap-2">
            <TestTube size={20} /> Test WhatsApp Integration
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Test Name</label>
              <input type="text" value={testData.name}
                onChange={(e) => setTestData({ ...testData, name: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-2 px-3 text-xs focus:outline-none focus:border-yellow-600" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Test Order ID</label>
              <input type="text" value={testData.orderId}
                onChange={(e) => setTestData({ ...testData, orderId: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-2 px-3 text-xs focus:outline-none focus:border-yellow-600" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Test Total</label>
              <input type="text" value={testData.total}
                onChange={(e) => setTestData({ ...testData, total: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-2 px-3 text-xs focus:outline-none focus:border-yellow-600" />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="tel"
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              className="flex-1 border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-yellow-600 text-sm"
              placeholder="Test WhatsApp number (+92 300 1234567)"
            />
            <button
              type="button"
              onClick={handleTestMessage}
              disabled={testing}
              className={`px-6 py-3 rounded-lg font-semibold text-sm btn-2d flex items-center justify-center gap-2 whitespace-nowrap ${
                testing ? 'opacity-70 cursor-not-allowed bg-gray-400' : 'bg-green-600 hover:bg-green-700'
              } text-white`}
            >
              {testing ? (
                <><span className="inline-block w-4 h-4 border-2 border-t-transparent border-white rounded-full anim-spin"></span> Sending...</>
              ) : (
                <><Send size={16} /> Send Test</>
              )}
            </button>
          </div>

          <p className="text-xs text-yellow-700 mt-3">
            ⚠️ Test number Meta Console mein "verified recipient" hona chahiye. Warna error aayega.
          </p>
        </div>

        {/* ============ SAVE ============ */}
        <div className="flex justify-end sticky bottom-4">
          <button
            type="submit"
            disabled={saving}
            className={`text-white py-3.5 px-10 rounded-lg font-bold transition flex items-center gap-2 shadow-xl ${
              saving ? 'opacity-70 cursor-not-allowed' : ''
            }`}
            style={{ backgroundColor: '#25D366' }}
          >
            <Save size={18} /> {saving ? 'Saving...' : 'Save Plugin Settings'}
          </button>
        </div>

      </form>

      <SuccessModal
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Success!"
        message="WhatsApp Order Confirmation plugin saved!"
        buttonText="Great!"
      />

    </div>
  );
};

export default WhatsAppOrderPlugin;